import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Search, RefreshCw, FileDown, Upload, Eye, Pencil, Phone, UserPlus, Trash2, Share2, Send } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { leadService, courseService, masterService, userService, csvService } from '../../services/api';
import { Button, Modal, Tab, Badge, Skeleton, EmptyState, Toast, Select } from '../../components/ui';
import { STATUS_COLORS, STATUS_NAMES, PRIORITIES } from '../../constants';
import { thStyle, tdStyle, iconBtnStyle } from '../../utils/tableStyles';
import CreateLeadForm from './CreateLeadForm';
import EditLeadForm from './EditLeadForm';
import LeadDetailView from './LeadDetailView';
import CallFeedbackForm from './CallFeedbackForm';
import AssignLeadModal from './AssignLeadModal';
import ConnectLeadModal from './ConnectLeadModal';
import BulkConnectModal from './BulkConnectModal';

const PAGE_SIZE = 20;

const LeadManagement = () => {
  const { dark } = useTheme();
  const { user, hasPermission, hasRole } = useAuth();

  const isRetailOULead = hasRole('RETAIL_OU_LEAD');
  const isCounsellor = hasRole('COUNSELLOR');
  const canCreate = hasPermission('leads', 'create');
  const canEdit = hasPermission('leads', 'update');
  const canDelete = hasPermission('leads', 'delete');
  const canAssign = hasPermission('leads', 'assign');
  const canImportCsv = hasPermission('csv', 'import') || hasPermission('csv-import', 'import');
  const canExport = hasPermission('leads', 'export');

  const [activeTab, setActiveTab] = useState(() => isCounsellor ? 'my' : 'all');
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [toast, setToast] = useState(null);

  // Master data
  const [courses, setCourses] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [counsellors, setCounsellors] = useState([]);

  // Modals
  const [createOpen, setCreateOpen] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [viewLeadId, setViewLeadId] = useState(null);
  const [callLead, setCallLead] = useState(null);
  const [assignLeadIds, setAssignLeadIds] = useState(null);
  const [deleteLeadId, setDeleteLeadId] = useState(null);
  const [connectLead, setConnectLead] = useState(null);
  const [bulkConnectLeads, setBulkConnectLeads] = useState(null);

  const searchTimer = useRef(null);

  useEffect(() => {
    courseService.getAll({ limit: 200 }).then(({ data }) => setCourses(data.data || [])).catch(() => {});
    masterService.getLeadSources().then(({ data }) => setLeadSources(data.data || [])).catch(() => {});
    userService.getCounsellors().then(({ data }) => setCounsellors(data.data || [])).catch(() => {});
  }, []);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: PAGE_SIZE,
        search: search || undefined,
        leadSource: filterSource || undefined,
        statusCode: filterStatus || undefined,
      };

      if (activeTab === 'unassigned') {
        params.unassigned = true;
      } else if (activeTab === 'success') {
        params.statusCode = 'ADMISSION_CONFIRMED';
      } else if (activeTab === 'lost') {
        params.statusCode = 'LOST_LEAD';
      }

      const { data } = await leadService.getAll(params);
      setLeads(data.data || []);
      setTotal(data.total || 0);
      setSelectedLeads([]);
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Error loading leads', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, search, filterSource, filterStatus, activeTab, isCounsellor, user]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleSearchChange = (val) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 400);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setPage(1);
    setSelectedLeads([]);
  };

  const handleFilterSource = (e) => {
    setFilterSource(e.target.value);
    setPage(1);
  };

  const handleFilterStatus = (e) => {
    setFilterStatus(e.target.value);
    setPage(1);
  };

  const toggleSelect = (id) => {
    setSelectedLeads(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map(l => l._id));
    }
  };

  const handleDelete = async () => {
    try {
      await leadService.delete(deleteLeadId);
      setToast({ msg: 'Lead deleted', type: 'success' });
      setDeleteLeadId(null);
      fetchLeads();
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Error deleting lead', type: 'error' });
    }
  };

  const handleExport = async () => {
    try {
      const params = {
        search: search || undefined,
        leadSource: filterSource || undefined,
        statusCode: filterStatus || undefined,
        export: true,
      };
      if (activeTab === 'unassigned') params.unassigned = true;
      const response = await leadService.getAll({ ...params, limit: 10000 });
      const rows = response.data.data || [];
      if (!rows.length) { setToast({ msg: 'No leads to export', type: 'warning' }); return; }
      const headers = ['Lead ID', 'Full Name', 'Mobile', 'Email', 'Course', 'Lead Source', 'Status', 'Priority', 'Counsellor', 'City', 'State', 'Created'];
      const csv = [headers, ...rows.map(l => [
        l.leadId, l.fullName, l.mobile, l.email || '',
        l.course?.name || '', l.leadSource?.name || '',
        l.currentStatus?.name || STATUS_NAMES[l.statusCode] || l.statusCode,
        l.priority || '', l.assignedCounsellor ? `${l.assignedCounsellor.firstName} ${l.assignedCounsellor.lastName}` : 'Unassigned',
        l.city || '', l.state || '', new Date(l.createdAt).toLocaleDateString(),
      ])].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `leads_export_${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setToast({ msg: 'Export failed', type: 'error' });
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const allSelected = leads.length > 0 && selectedLeads.length === leads.length;

  const buildTabs = () => {
    if (isCounsellor) {
      return [
        { key: 'my', label: 'My Leads' },
        { key: 'success', label: 'Success' },
        { key: 'lost', label: 'Lost Leads' },
      ];
    }
    const tabs = [{ key: 'all', label: 'All Leads' }];
    if (isRetailOULead) tabs.push({ key: 'unassigned', label: 'Unassigned' });
    return tabs;
  };

  const tabLocksStatus = activeTab === 'success' || activeTab === 'lost';

  const cardStyle = {
    background: dark ? '#0F172A' : '#fff',
    border: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}`,
    borderRadius: 16, overflow: 'hidden',
  };

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: dark ? '#F1F5F9' : '#0F172A', margin: 0 }}>Leads</h1>
          <p style={{ fontSize: 13, color: dark ? '#64748B' : '#94A3B8', margin: '4px 0 0' }}>
            {total.toLocaleString()} total leads
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canExport && (
            <Button variant="outline" size="sm" icon={FileDown} onClick={handleExport}>Export CSV</Button>
          )}
          {canImportCsv && (
            <Button variant="outline" size="sm" icon={Upload} onClick={() => window.location.href = '/csv'}>Import CSV</Button>
          )}
          {canCreate && (
            <Button size="sm" icon={Plus} onClick={() => setCreateOpen(true)}>Add Lead</Button>
          )}
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedLeads.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
          background: dark ? '#1E3A5F' : '#EFF6FF', border: `1px solid ${dark ? '#2563EB' : '#BFDBFE'}`,
          borderRadius: 10, marginBottom: 16,
        }}>
          <span style={{ fontSize: 13, color: dark ? '#93C5FD' : '#1D4ED8', fontWeight: 600 }}>
            {selectedLeads.length} lead{selectedLeads.length > 1 ? 's' : ''} selected
          </span>
          <Button
            size="sm"
            icon={Send}
            onClick={() => setBulkConnectLeads(leads.filter(l => selectedLeads.includes(l._id)))}
          >
            Send Message
          </Button>
          {canAssign && (
            <Button size="sm" icon={UserPlus} onClick={() => setAssignLeadIds(selectedLeads)}>
              Assign to Counsellor
            </Button>
          )}
          <button style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: dark ? '#64748B' : '#94A3B8' }} onClick={() => setSelectedLeads([])}>
            Clear
          </button>
        </div>
      )}

      <div style={cardStyle}>
        {/* Tabs */}
        <div style={{ padding: '16px 20px 0', borderBottom: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}` }}>
          <Tab tabs={buildTabs()} active={activeTab} onChange={handleTabChange} />
        </div>

        {/* Filters row */}
        <div style={{ display: 'flex', gap: 10, padding: '14px 20px', flexWrap: 'wrap', alignItems: 'center', borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={14} color={dark ? '#475569' : '#94A3B8'} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              value={searchInput}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder="Search by name, mobile, lead ID..."
              style={{
                width: '100%', padding: '7px 10px 7px 30px', borderRadius: 8, fontSize: 13,
                border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`,
                background: dark ? '#1E293B' : '#F8FAFC',
                color: dark ? '#F1F5F9' : '#0F172A', outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Lead Source filter (highlighted for RETAIL_OU_LEAD) */}
          <select
            value={filterSource}
            onChange={handleFilterSource}
            style={{
              padding: '7px 10px', borderRadius: 8, fontSize: 13,
              border: `1px solid ${isRetailOULead && !filterSource ? '#3B82F6' : (dark ? '#334155' : '#E2E8F0')}`,
              background: dark ? '#1E293B' : '#F8FAFC',
              color: dark ? '#F1F5F9' : '#0F172A', outline: 'none', cursor: 'pointer',
              boxShadow: isRetailOULead && !filterSource ? '0 0 0 3px rgba(59,130,246,0.1)' : 'none',
            }}
          >
            <option value="">All Lead Sources</option>
            {leadSources.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
          </select>

          {/* Status filter — hidden when tab already locks the status */}
          {!tabLocksStatus && (
            <select
              value={filterStatus}
              onChange={handleFilterStatus}
              style={{
                padding: '7px 10px', borderRadius: 8, fontSize: 13,
                border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`,
                background: dark ? '#1E293B' : '#F8FAFC',
                color: dark ? '#F1F5F9' : '#0F172A', outline: 'none', cursor: 'pointer',
              }}
            >
              <option value="">All Statuses</option>
              {Object.entries(STATUS_NAMES).map(([code, name]) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
          )}

          <button
            onClick={() => { setSearch(''); setSearchInput(''); setFilterSource(''); setFilterStatus(''); setPage(1); }}
            title="Reset filters"
            style={{ ...iconBtnStyle(dark) }}
          >
            <RefreshCw size={14} />
          </button>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          {loading ? (
            <div style={{ padding: 20 }}><Skeleton rows={8} cols={6} /></div>
          ) : leads.length === 0 ? (
            <div style={{ padding: 40 }}>
              <EmptyState
              title={
                activeTab === 'unassigned' ? 'No unassigned leads' :
                activeTab === 'success' ? 'No successful admissions yet' :
                activeTab === 'lost' ? 'No lost leads' :
                'No leads found'
              }
              description={
                activeTab === 'unassigned' ? 'All leads have been assigned to counsellors.' :
                activeTab === 'success' ? 'Leads that reach Admission Confirmed will appear here.' :
                activeTab === 'lost' ? 'Leads marked as lost will appear here.' :
                'Try adjusting your filters or create a new lead.'
              }
            />
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: dark ? '#0F172A' : '#F8FAFC' }}>
                <tr>
                  {(canAssign || canEdit) && (
                    <th style={{ ...thStyle(dark), width: 36 }}>
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} style={{ cursor: 'pointer' }} />
                    </th>
                  )}
                  <th style={thStyle(dark)}>Lead ID</th>
                  <th style={thStyle(dark)}>Name</th>
                  <th style={thStyle(dark)}>Mobile</th>
                   <th style={thStyle(dark)}>Email</th>
                  <th style={thStyle(dark)}>Course</th>
                  <th style={thStyle(dark)}>Source</th>
                  <th style={thStyle(dark)}>Status</th>
                  <th style={thStyle(dark)}>Priority</th>
                  <th style={thStyle(dark)}>Counsellor</th>
                  <th style={thStyle(dark)}>Created</th>
                  <th style={thStyle(dark)}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map(lead => (
                  <tr key={lead._id} style={{
                    borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}`,
                    background: selectedLeads.includes(lead._id)
                      ? (dark ? '#1E3A5F' : '#EFF6FF')
                      : (dark ? '#0F172A' : '#fff'),
                  }}>
                    {(canAssign || canEdit) && (
                      <td style={tdStyle(dark)}>
                        <input type="checkbox" checked={selectedLeads.includes(lead._id)} onChange={() => toggleSelect(lead._id)} style={{ cursor: 'pointer' }} />
                      </td>
                    )}
                    <td style={{ ...tdStyle(dark), fontFamily: 'monospace', fontSize: 11 }}>{lead.leadId}</td>
                    <td style={{ ...tdStyle(dark), fontWeight: 600 }}>{lead.fullName}</td>
                    <td style={tdStyle(dark)}>{lead.mobile}</td>
                      <td style={tdStyle(dark)}>{lead.email}</td>
                    <td style={tdStyle(dark)}>{lead.course?.name || '—'}</td>
                    <td style={tdStyle(dark)}>{lead.leadSource?.name || '—'}</td>
                    <td style={tdStyle(dark)}>
                      <Badge color={lead.currentStatus?.color || STATUS_COLORS[lead.statusCode] || '#6B7280'}>
                        {lead.currentStatus?.name || STATUS_NAMES[lead.statusCode] || lead.statusCode}
                      </Badge>
                    </td>
                    <td style={tdStyle(dark)}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: PRIORITIES[lead.priority] || '#6B7280', textTransform: 'capitalize' }}>
                        {lead.priority || '—'}
                      </span>
                    </td>
                    <td style={tdStyle(dark)}>
                      {lead.assignedCounsellor
                        ? <span style={{ fontSize: 12 }}>{lead.assignedCounsellor.firstName} {lead.assignedCounsellor.lastName || ''}</span>
                        : <Badge color="#6B7280">Unassigned</Badge>
                      }
                    </td>
                    <td style={{ ...tdStyle(dark), fontSize: 11 }}>{new Date(lead.createdAt).toLocaleDateString()}</td>
                    <td style={tdStyle(dark)}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button title="View" style={iconBtnStyle(dark)} onClick={() => setViewLeadId(lead._id)}>
                          <Eye size={14} />
                        </button>
                        {canEdit && (
                          <button title="Edit" style={iconBtnStyle(dark)} onClick={() => setEditLead(lead)}>
                            <Pencil size={14} />
                          </button>
                        )}
                        <button title="Log Call" style={iconBtnStyle(dark)} onClick={() => setCallLead(lead)}>
                          <Phone size={14} />
                        </button>
                        <button
                          title="Connect via WhatsApp / Email"
                          style={{
                            ...iconBtnStyle(dark),
                            color: '#25D366',
                            borderColor: dark ? '#166534' : '#BBF7D0',
                          }}
                          onClick={() => setConnectLead(lead)}
                        >
                          <Share2 size={14} />
                        </button>
                        {canAssign && (
                          <button title="Assign" style={iconBtnStyle(dark)} onClick={() => setAssignLeadIds([lead._id])}>
                            <UserPlus size={14} />
                          </button>
                        )}
                        {canDelete && (
                          <button title="Delete" style={{ ...iconBtnStyle(dark), color: '#EF4444', borderColor: '#FCA5A5' }} onClick={() => setDeleteLeadId(lead._id)}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderTop: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
            <span style={{ fontSize: 12, color: dark ? '#64748B' : '#94A3B8' }}>
              Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let p;
                if (totalPages <= 5) p = i + 1;
                else if (page <= 3) p = i + 1;
                else if (page >= totalPages - 2) p = totalPages - 4 + i;
                else p = page - 2 + i;
                return (
                  <button key={p} onClick={() => setPage(p)} style={{
                    width: 32, height: 32, borderRadius: 6, border: `1px solid ${p === page ? '#3B82F6' : (dark ? '#334155' : '#E2E8F0')}`,
                    background: p === page ? '#3B82F6' : 'transparent',
                    color: p === page ? '#fff' : (dark ? '#CBD5E1' : '#475569'),
                    fontSize: 12, cursor: 'pointer', fontWeight: p === page ? 700 : 400,
                  }}>
                    {p}
                  </button>
                );
              })}
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>

      {/* Create Lead Modal */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Create New Lead" size="lg">
        <CreateLeadForm
          courses={courses}
          leadSources={leadSources}
          counsellors={counsellors}
          onClose={() => setCreateOpen(false)}
          onCreated={fetchLeads}
        />
      </Modal>

      {/* Edit Lead Modal */}
      <Modal open={!!editLead} onClose={() => setEditLead(null)} title="Edit Lead" size="lg">
        {editLead && (
          <EditLeadForm
            lead={editLead}
            courses={courses}
            leadSources={leadSources}
            onClose={() => setEditLead(null)}
            onUpdated={fetchLeads}
          />
        )}
      </Modal>

      {/* Lead Detail Modal */}
      <Modal open={!!viewLeadId} onClose={() => setViewLeadId(null)} title="Lead Details" size="xl">
        {viewLeadId && (
          <LeadDetailView
            leadId={viewLeadId}
            onCall={() => {
              const lead = leads.find(l => l._id === viewLeadId);
              if (lead) { setViewLeadId(null); setCallLead(lead); }
            }}
          />
        )}
      </Modal>

      {/* Call Feedback Modal */}
      <Modal open={!!callLead} onClose={() => setCallLead(null)} title="Log Call Feedback" size="md">
        {callLead && (
          <CallFeedbackForm
            lead={callLead}
            onClose={() => setCallLead(null)}
            onUpdated={fetchLeads}
          />
        )}
      </Modal>

      {/* Assign Modal */}
      <AssignLeadModal
        open={!!assignLeadIds}
        leadIds={assignLeadIds || []}
        onClose={() => setAssignLeadIds(null)}
        onAssigned={() => { setAssignLeadIds(null); fetchLeads(); }}
      />

      {/* Connect Lead Modal */}
      <ConnectLeadModal
        open={!!connectLead}
        lead={connectLead}
        onClose={() => setConnectLead(null)}
      />

      {/* Bulk Connect Modal */}
      <BulkConnectModal
        open={!!bulkConnectLeads}
        leads={bulkConnectLeads || []}
        onClose={() => setBulkConnectLeads(null)}
      />

      {/* Delete Confirm Modal */}
      <Modal open={!!deleteLeadId} onClose={() => setDeleteLeadId(null)} title="Confirm Delete" size="sm">
        <p style={{ fontSize: 14, color: dark ? '#CBD5E1' : '#334155', margin: '0 0 20px' }}>
          Are you sure you want to delete this lead? This action cannot be undone.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="outline" onClick={() => setDeleteLeadId(null)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete}>Delete Lead</Button>
        </div>
      </Modal>
    </div>
  );
};

export default LeadManagement;
